/**
 * Login — the page unauthenticated users see.
 *
 * Single button: "Sign In with Cognito." Clicking it kicks off the
 * OAuth2 PKCE redirect to Cognito Hosted UI (see lib/auth.startLogin).
 *
 * If a user lands here while already authenticated (e.g. typed /login
 * directly), we send them home instead.
 */
import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { startLogin } from "../lib/auth";

export function Login() {
  const { status } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Already signed in? Send home.
  if (status === "authenticated") {
    return <Navigate to="/ai" replace />;
  }

  // Still booting? Show nothing rather than flicker.
  if (status === "loading") {
    return null;
  }

  async function handleSignIn() {
    setBusy(true);
    setError(null);
    try {
      await startLogin();
      // startLogin navigates to Cognito; we won't reach this line.
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <div
      className="flex min-h-screen items-center justify-center px-4"
      style={{ background: "var(--bg)" }}
    >
      <div
        className="rounded-lg border hairline p-8 w-full max-w-sm"
        style={{ background: "var(--bg-elev)" }}
      >
        <div className="text-center mb-6">
          <h1
            className="font-serif text-[28px] leading-tight mb-1"
            style={{ color: "var(--ink)" }}
          >
            ASHI
          </h1>
          <div
            className="text-[10.5px] font-mono uppercase tracking-[0.18em]"
            style={{ color: "var(--brass)" }}
          >
            Analyst Portal
          </div>
        </div>

        <button
          onClick={handleSignIn}
          disabled={busy}
          className="w-full rounded-md py-2.5 px-4 font-serif text-[14.5px] transition-opacity disabled:opacity-50"
          style={{
            background: "var(--ink)",
            color: "var(--bg)",
          }}
        >
          {busy ? "Redirecting…" : "Sign In"}
        </button>

        {error && (
          <div
            className="mt-4 text-[13px] font-mono"
            style={{ color: "var(--oxblood)" }}
          >
            {error}
          </div>
        )}

        <p
          className="mt-6 text-[12.5px] font-serif italic text-center"
          style={{ color: "var(--ink-3)" }}
        >
          Access is invitation-only. Contact an administrator for an account.
        </p>
      </div>
    </div>
  );
}